import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Building2, ShieldCheck, Clock, CalendarCheck, CheckCircle2, Check, X, UserRound,
  Eye, MailQuestion, Mail, RotateCw, Send,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatCard, PageHeader, UserAvatar, StatusBadge, EmptyState, Spinner, Pill, VerifiedBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fmtDate, fmtTime } from "@/lib/constants";
import { ExpertDetailSheet } from "./admin/ExpertDetailSheet";
import { EmailStatus } from "./admin/EmailStatus";


export default function AdminDashboard() {
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [experts, setExperts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reqFor, setReqFor] = useState(null);
  const [reqMsg, setReqMsg] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailDetail, setEmailDetail] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [demos, setDemos] = useState([]);

  const load = () => {
    api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/admin/experts").then(({ data }) => setExperts(data)).catch(() => {});
    api.get("/admin/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    api.get("/admin/emails").then(({ data }) => setEmails(data)).catch(() => {});
    api.get("/admin/demo-requests").then(({ data }) => setDemos(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setDemoStatus = async (id, status) => {
    try {
      await api.patch(`/admin/demo-requests/${id}`, { status });
      setDemos((list) => list.map((x) => (x.request_id === id ? { ...x, status } : x)));
    } catch (err) { toast.error(apiError(err)); }
  };

  const applyEmailUpdate = (id, data) => {
    const patch = { status: data.status, attempts: data.attempts, provider_id: data.provider_id, error: data.error };
    setEmails((list) => list.map((m) => (m.email_id === id ? { ...m, ...patch } : m)));
    setEmailDetail((d) => (d && d.email_id === id ? { ...d, ...patch } : d));
  };

  const retryEmail = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/admin/emails/${id}/retry`);
      applyEmailUpdate(id, data);
      if (data.ok) toast.success(t("admin.retrySuccess"));
      else toast.error(t("admin.retryStillFailed"));
    } catch (err) { toast.error(apiError(err)); } finally { setRetrying(null); }
  };

  const retryAllFailed = async () => {
    setRetrying("all");
    try {
      const { data } = await api.post(`/admin/emails/retry-failed`);
      toast.success(t("admin.retryAllDone", { delivered: data.delivered || 0, total: data.candidates || 0 }));
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setRetrying(null); }
  };

  const decide = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/experts/${id}/${action}`, action === "approve" ? { verified: true } : {});
      toast.success(action === "approve" ? t("admin.toastVerified") : t("admin.toastRejected"));
      setDetail(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  const openRequest = (e) => {
    setReqFor(e);
    setReqMsg(t("admin.requestTemplate", { name: (e.name || "there").split(" ")[0] }));
  };

  const sendRequest = async () => {
    if (!reqMsg.trim()) { toast.error(t("admin.toastAddMessage")); return; }
    setReqBusy(true);
    try {
      await api.post(`/admin/experts/${reqFor.expert_id}/request-info`, { message: reqMsg });
      toast.success(t("admin.toastMessageSent", { name: reqFor.name }));
      setReqFor(null); setDetail(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setReqBusy(false); }
  };

  if (!a) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  const pending = experts.filter((e) => e.status === "pending");
  const newDemos = demos.filter((d) => d.status === "new").length;
  const cards = [
    { icon: Building2, label: t("admin.cardCompanies"), value: a.companies, sub: t("admin.cardCompaniesSub"), accent: true },
    { icon: UserRound, label: t("admin.cardEmployees"), value: a.employees, sub: t("admin.cardEmployeesSub") },
    { icon: ShieldCheck, label: t("admin.cardVerified"), value: a.experts_approved, sub: t("admin.cardVerifiedSub") },
    { icon: Clock, label: t("admin.cardPending"), value: a.experts_pending, sub: a.experts_pending ? t("admin.cardPendingReview") : t("admin.cardPendingClear") },
    { icon: CalendarCheck, label: t("admin.cardSessions"), value: a.bookings, sub: t("admin.cardSessionsSub") },
    { icon: CheckCircle2, label: t("admin.cardCompleted"), value: a.completed, sub: t("admin.cardCompletedSub") },
  ];

  return (
    <AppShell>
      <PageHeader eyebrow={t("admin.eyebrow")} title={t("admin.title")} description={t("admin.subtitle")} />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-5">
        {cards.map((c, i) => <div key={c.label} className={`animate-fade-up d${(i % 6) + 1}`}><StatCard {...c} /></div>)}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-lg">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-md gap-1.5">
            {t("admin.tabQueue")} {pending.length > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="experts" data-testid="admin-tab-experts" className="rounded-md">{t("admin.tabExperts")}</TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies" className="rounded-md">{t("admin.tabCompanies")}</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails" className="rounded-md gap-1.5"><Mail size={14} /> {t("admin.tabEmails")}</TabsTrigger>
          <TabsTrigger value="demos" data-testid="admin-tab-demos" className="rounded-md gap-1.5">
            <CalendarCheck size={14} /> {t("admin.tabDemos")} {newDemos > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">{newDemos}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          {pending.length === 0 ? (
            <EmptyState icon={ShieldCheck} title={t("admin.queueEmpty")} description={t("admin.queueEmptyDesc")} />
          ) : (
            <div className="space-y-3">
              {pending.map((e) => (
                <div key={e.expert_id} data-testid={`pending-expert-${e.expert_id}`}
                  className="card-milled rounded-[0.7rem] p-5 flex flex-col sm:flex-row sm:items-center gap-4 transition-colors hover:border-primary/40">
                  <button onClick={() => setDetail(e)} data-testid={`view-expert-${e.expert_id}`} className="flex items-center gap-4 min-w-0 flex-1 text-left group">
                    <UserAvatar src={fileUrl(e.picture)} name={e.name} size={52} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium group-hover:text-primary transition-colors">{e.name}</p>
                        {e.info_requested && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 text-warning px-2 py-0.5 text-[11px] font-medium"><MailQuestion size={11} /> {t("admin.infoRequested")}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{e.title || "—"}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{e.headline}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">{(e.fields || []).map((f) => <Pill key={f} className="bg-primary/10 text-primary">{f}</Pill>)}</div>
                    </div>
                  </button>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`review-expert-${e.expert_id}`} variant="outline" onClick={() => setDetail(e)} className="rounded-lg gap-1.5 bg-card"><Eye size={15} /> {t("admin.viewProfile")}</Button>
                    <Button data-testid={`reject-expert-${e.expert_id}`} variant="outline" disabled={busy === e.expert_id} onClick={() => decide(e.expert_id, "reject")} className="rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5"><X size={15} /> {t("admin.reject")}</Button>
                    <Button data-testid={`approve-expert-${e.expert_id}`} disabled={busy === e.expert_id} onClick={() => decide(e.expert_id, "approve")} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">{busy === e.expert_id ? <Spinner /> : <><Check size={15} /> {t("admin.verify")}</>}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="experts" className="mt-5">
          <div className="card-milled rounded-[0.7rem] overflow-hidden">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent border-border">
                <TableHead>{t("admin.colExpert")}</TableHead><TableHead className="hidden md:table-cell">{t("admin.colFields")}</TableHead>
                <TableHead className="text-center">{t("admin.colRating")}</TableHead><TableHead className="text-center">{t("admin.colStatus")}</TableHead><TableHead className="text-right">{t("admin.colAction")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {experts.map((e) => (
                  <TableRow key={e.expert_id} data-testid={`admin-expert-${e.expert_id}`} className="border-border hover:bg-secondary/40 cursor-pointer" onClick={() => setDetail(e)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <UserAvatar src={fileUrl(e.picture)} name={e.name} size={36} />
                        <div><div className="flex items-center gap-1.5"><span className="font-medium text-sm">{e.name}</span>{e.verified && <VerifiedBadge label="" />}</div><p className="text-xs text-muted-foreground">{e.title}</p></div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell"><div className="flex flex-wrap gap-1">{(e.fields || []).slice(0, 2).map((f) => <Pill key={f}>{f}</Pill>)}</div></TableCell>
                    <TableCell className="text-center text-sm tabular">{e.rating_avg?.toFixed(1) || "—"}</TableCell>
                    <TableCell className="text-center"><StatusBadge status={e.status} /></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" className="rounded-lg gap-1.5" data-testid={`view-row-${e.expert_id}`} onClick={(ev) => { ev.stopPropagation(); setDetail(e); }}><Eye size={14} /> {t("admin.view")}</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="companies" className="mt-5">
          {companies.length === 0 ? (
            <EmptyState icon={Building2} title={t("admin.companiesEmpty")} description={t("admin.companiesEmptyDesc")} />
          ) : (
            <div className="card-milled rounded-[0.7rem] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border">
                  <TableHead>{t("admin.colCompany")}</TableHead><TableHead className="hidden sm:table-cell">{t("admin.colIndustry")}</TableHead>
                  <TableHead className="text-center">{t("admin.colEmployees")}</TableHead><TableHead className="text-center">{t("admin.colNetwork")}</TableHead>
                  <TableHead className="text-center">{t("admin.colSessions")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {companies.map((c) => (
                    <TableRow key={c.company_id} data-testid={`admin-company-${c.company_id}`} className="border-border hover:bg-secondary/40">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary"><Building2 size={16} /></span>
                          <span className="font-medium text-sm">{c.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{c.industry || "—"}</TableCell>
                      <TableCell className="text-center tabular">{c.employees}</TableCell>
                      <TableCell className="text-center tabular">{c.network_size}</TableCell>
                      <TableCell className="text-center tabular">{c.sessions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="emails" className="mt-5">
          <div className="mb-4 flex items-start justify-between gap-3 rounded-[0.7rem] border border-border bg-secondary/40 p-4">
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">{t("admin.outboxTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("admin.outboxDesc")}</p>
              </div>
            </div>
            {emails.filter((m) => m.status === "failed").length > 0 && (
              <Button size="sm" variant="outline" data-testid="email-retry-all" onClick={retryAllFailed} disabled={retrying === "all"}
                className="rounded-lg gap-1.5 shrink-0 bg-card">
                {retrying === "all" ? <Spinner /> : <><RotateCw size={14} /> {t("admin.retryAll")} ({emails.filter((m) => m.status === "failed").length})</>}
              </Button>
            )}
          </div>
          {emails.length === 0 ? (
            <EmptyState icon={Mail} title={t("admin.emailsEmpty")} description={t("admin.emailsEmptyDesc")} />
          ) : (
            <div className="card-milled rounded-[0.7rem] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border">
                  <TableHead>{t("admin.colRecipient")}</TableHead><TableHead>{t("admin.colSubject")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("admin.colType")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("admin.colSent")}</TableHead>
                  <TableHead className="text-right">{t("admin.colStatus")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {emails.map((m) => (
                    <TableRow key={m.email_id} data-testid={`email-${m.email_id}`} className="border-border hover:bg-secondary/40 cursor-pointer" onClick={() => setEmailDetail(m)}>
                      <TableCell className="text-sm font-medium">{m.to}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[16rem] truncate">{m.subject}</TableCell>
                      <TableCell className="hidden md:table-cell"><Pill className="capitalize">{(m.kind || "").replace(/_/g, " ")}</Pill></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground whitespace-nowrap">{fmtDate(m.created_at)} · {fmtTime(m.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <EmailStatus status={m.status} />
                          {m.status === "failed" && (
                            <Button size="sm" variant="outline" data-testid={`email-retry-${m.email_id}`}
                              onClick={(e) => { e.stopPropagation(); retryEmail(m.email_id); }} disabled={retrying === m.email_id}
                              className="h-7 rounded-lg px-2 text-xs gap-1 bg-card">
                              {retrying === m.email_id ? <Spinner /> : <><RotateCw size={12} /> {t("admin.retry")}</>}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="demos" className="mt-5">
          <div className="mb-4 flex items-start gap-3 rounded-[0.7rem] border border-primary/20 bg-primary/5 p-4">
            <CalendarCheck size={18} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">{t("admin.demosBannerTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("admin.demosBannerDesc")}</p>
            </div>
          </div>
          {demos.length === 0 ? (
            <EmptyState icon={CalendarCheck} title={t("admin.demosEmpty")} description={t("admin.demosEmptyDesc")} />
          ) : (
            <div className="card-milled rounded-[0.7rem] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent border-border">
                  <TableHead>{t("admin.colName")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("admin.colCompany")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("admin.colTeamSize")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("admin.colDate")}</TableHead>
                  <TableHead className="text-right">{t("admin.colStatus")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {demos.map((d) => (
                    <TableRow key={d.request_id} data-testid={`demo-${d.request_id}`} className="border-border hover:bg-secondary/40 align-top">
                      <TableCell>
                        <p className="text-sm font-medium">{d.name}</p>
                        <a href={`mailto:${d.work_email}`} className="text-xs text-primary hover:underline">{d.work_email}</a>
                        {d.message && <p className="text-xs text-muted-foreground mt-1 max-w-[22rem] line-clamp-2">{d.message}</p>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{d.company || "—"}</TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{d.team_size || "—"}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-muted-foreground whitespace-nowrap">{fmtDate(d.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Select value={d.status} onValueChange={(v) => setDemoStatus(d.request_id, v)}>
                          <SelectTrigger data-testid={`demo-status-${d.request_id}`} className="h-8 w-32 rounded-lg ml-auto text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">{t("admin.demoStatusNew")}</SelectItem>
                            <SelectItem value="contacted">{t("admin.demoStatusContacted")}</SelectItem>
                            <SelectItem value="closed">{t("admin.demoStatusClosed")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ExpertDetailSheet expert={detail} onClose={() => setDetail(null)} onDecide={decide} onRequestInfo={openRequest} busy={busy === detail?.expert_id} />

      <Dialog open={!!reqFor} onOpenChange={(o) => !o && setReqFor(null)}>
        <DialogContent className="rounded-[0.7rem]">
          <DialogHeader><DialogTitle>{t("admin.reqDialogTitle", { name: reqFor?.name })}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1"><Trans i18nKey="admin.reqDialogDesc" components={{ b: <span className="font-medium text-foreground" /> }} /></DialogDescription>
          <Textarea data-testid="request-message" value={reqMsg} onChange={(e) => setReqMsg(e.target.value)} className="rounded-lg min-h-44" />
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setReqFor(null)}>{t("common.cancel")}</Button>
            <Button data-testid="send-request" onClick={sendRequest} disabled={reqBusy} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
              {reqBusy ? <Spinner /> : <><Send size={15} /> {t("admin.sendMessage")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailDetail} onOpenChange={(o) => !o && setEmailDetail(null)}>
        <DialogContent className="rounded-[0.7rem] max-w-lg">
          <DialogHeader><DialogTitle className="text-base pr-6">{emailDetail?.subject}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Email preview</DialogDescription>
          <div className="text-sm space-y-2">
            <div className="flex gap-3"><span className="text-muted-foreground w-14 shrink-0">{t("admin.emailTo")}</span><span className="font-medium">{emailDetail?.to}</span></div>
            <div className="flex gap-3 items-center"><span className="text-muted-foreground w-14 shrink-0">{t("admin.emailType")}</span><Pill className="capitalize">{(emailDetail?.kind || "").replace(/_/g, " ")}</Pill></div>
            <div className="flex gap-3"><span className="text-muted-foreground w-14 shrink-0">{t("admin.emailSent")}</span><span>{emailDetail && `${fmtDate(emailDetail.created_at)} · ${fmtTime(emailDetail.created_at)}`}</span></div>
            <div className="flex gap-3 items-center">
              <span className="text-muted-foreground w-14 shrink-0">{t("admin.emailStatus")}</span>
              <EmailStatus status={emailDetail?.status} />
              {emailDetail?.attempts ? <span className="text-xs text-muted-foreground">· {t("admin.emailAttempts")}: {emailDetail.attempts}</span> : null}
            </div>
            {emailDetail?.error && (
              <div className="flex gap-3"><span className="text-muted-foreground w-14 shrink-0">{t("admin.emailError")}</span><span className="text-destructive text-xs break-all">{emailDetail.error}</span></div>
            )}
          </div>
          <div className="mt-1 rounded-lg border border-border bg-secondary/40 p-4 text-sm whitespace-pre-line leading-relaxed max-h-72 overflow-y-auto">{emailDetail?.body}</div>
          {emailDetail?.status === "mocked" && <p className="text-xs text-warning flex items-center gap-1.5"><Mail size={13} /> {t("admin.emailMockedNote")}</p>}
          {emailDetail?.status === "failed" && (
            <Button data-testid="email-detail-retry" onClick={() => retryEmail(emailDetail.email_id)} disabled={retrying === emailDetail?.email_id}
              className="rounded-lg gap-1.5 self-start active:scale-[0.98] transition-transform">
              {retrying === emailDetail?.email_id ? <Spinner /> : <><RotateCw size={14} /> {t("admin.retry")}</>}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
